import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  BarChart3,
  Boxes,
  BriefcaseBusiness,
  CreditCard,
  FileText,
  Home,
  CalendarDays,
  Receipt,
  ClipboardCheck,
  Settings,
  TrendingDown,
  TrendingUp,
  Users2,
  ChevronRight,
  Building2,
  BadgeEuro,
  PanelLeftClose,
  PanelLeftOpen,
  GripVertical,
  Calculator,
} from "lucide-react";

const iconMap = {
  dashboard: Home,
  clients: Building2,
  projects: BriefcaseBusiness,
  offers: FileText,
  kalkulation: Calculator,
  calendar: CalendarDays,
  timesheets: ClipboardCheck,
  projectProfit: BadgeEuro,
  income: TrendingUp,
  expenses: TrendingDown,
  payments: CreditCard,
  invoice: FileText,
  materials: Boxes,
  workers: Users2,
  reports: BarChart3,
  settings: Settings,
};

const sectionMap = {
  dashboard: "main",
  clients: "main",
  projects: "main",
  offers: "main",
  kalkulation: "main",
  calendar: "main",
  timesheets: "main",
  projectProfit: "finance",
  invoice: "main",
  income: "finance",
  payments: "finance",
  expenses: "finance",
  reports: "finance",
  materials: "operations",
  workers: "operations",
  settings: "system",
};

const sectionMeta = {
  main: { sq: "KRYESORE", de: "ÜBERSICHT" },
  finance: { sq: "FINANCA & ANALITIKA", de: "FINANZEN & ANALYTIK" },
  operations: { sq: "OPERACIONE", de: "OPERATIONEN" },
  system: { sq: "SISTEMI", de: "SYSTEM" },
};

export default function DesktopSidebar({
  menu,
  active,
  setActive,
  t,
  lang,
  setLang,
  company,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [width, setWidth] = useState(250);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef(null);
  const isGerman = lang === "de";
  const companyName = company?.companyName || t?.appName || "BauHeld";

  const menuItems = useMemo(() => menu || [], [menu]);

  const groupedMenu = useMemo(() => {
    const buckets = {
      main: [],
      finance: [],
      operations: [],
      system: [],
    };
    menuItems.forEach((item) => {
      const section = sectionMap[item.key] || "main";
      buckets[section].push(item);
    });
    return buckets;
  }, [menuItems]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newWidth = e.clientX;
    if (newWidth >= 180 && newWidth <= 400) {
      setWidth(newWidth);
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const renderSection = (sectionKey, items) => {
    if (!items?.length) return null;

    return (
      <div key={sectionKey} className="space-y-2">
        {!isCollapsed && (
          <div className="px-3 pb-1 text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-500">
            {sectionMeta[sectionKey]?.[lang] ||
              sectionMeta[sectionKey]?.sq ||
              sectionKey}
          </div>
        )}

        <div className="space-y-1.5">
          {items.map((item) => {
            const Icon = iconMap[item.key] || Receipt;
            const isActive = active === item.key;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActive(item.key)}
                className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl px-3 py-2.5 text-left transition-all duration-200 ${
                  isActive
                    ? "border border-blue-400/30 bg-white/10 text-white shadow-[0_0_0_1px_rgba(96,165,250,0.18)]"
                    : "border border-transparent text-slate-300 hover:bg-white/5 hover:text-white"
                } ${isCollapsed ? "justify-center px-2" : ""}`}
              >
                {isActive && !isCollapsed ? (
                  <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-blue-400 shadow-[0_0_18px_rgba(96,165,250,0.8)]" />
                ) : null}

                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl transition ${
                    isActive
                      ? "bg-white/15 text-blue-300"
                      : "bg-white/5 text-slate-300 group-hover:bg-white/10 group-hover:text-white"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>

                {!isCollapsed && (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">
                        {item.label}
                      </div>
                    </div>

                    <ChevronRight
                      className={`h-4 w-4 shrink-0 transition ${
                        isActive
                          ? "text-blue-300"
                          : "text-slate-500 group-hover:text-slate-300"
                      }`}
                    />
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className="relative hidden xl:block"
      style={{ width: isCollapsed ? 80 : width }}
    >
      <div
        className="sticky top-0 flex min-h-screen flex-col border-r border-white/10 bg-gradient-to-b from-[#071227] via-[#081833] to-[#061126]"
        style={{ width: isCollapsed ? 80 : width }}
      >
        {/* Header with collapse button */}
        <div className="flex items-center justify-between px-4 pb-2 pt-5">
          {!isCollapsed && (
            <div className="flex items-center gap-3 rounded-[28px] border border-white/10 bg-white/5 px-4 py-4 shadow-[0_20px_50px_rgba(2,6,23,0.28)] backdrop-blur-xl">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-cyan-400 to-indigo-500 shadow-lg shadow-blue-500/30 ring-1 ring-white/10">
                {company?.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt="Company logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-black text-white">
                    {(companyName || "B").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.26em] text-slate-400">
                  BauHeld Premium
                </p>
                <h2 className="truncate text-lg font-bold text-white">
                  {companyName}
                </h2>
                <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                  <BriefcaseBusiness className="h-3.5 w-3.5" />
                  <span>{isGerman ? "Office Panel" : "Office Panel"}</span>
                </div>
              </div>
            </div>
          )}

          {isCollapsed && (
            <div className="flex w-full justify-center">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-cyan-400 to-indigo-500 shadow-lg shadow-blue-500/30 ring-1 ring-white/10">
                {company?.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt="Company logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-black text-white">
                    {(companyName || "B").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          )}

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-3 top-6 flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-[#0a1a2f] text-slate-400 hover:text-white"
          >
            {isCollapsed ? (
              <PanelLeftOpen className="h-3.5 w-3.5" />
            ) : (
              <PanelLeftClose className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        {!isCollapsed && <div className="mx-4 mb-4 h-px bg-white/10" />}

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 pb-6">
          {renderSection("main", groupedMenu.main)}
          {renderSection("finance", groupedMenu.finance)}
          {renderSection("operations", groupedMenu.operations)}
          {renderSection("system", groupedMenu.system)}
        </nav>
      </div>

      {/* Drag handle */}
      {!isCollapsed && (
        <div
          className="absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize hover:bg-blue-500/20"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 transform items-center justify-center">
            <GripVertical className="h-4 w-4 text-slate-500" />
          </div>
        </div>
      )}
    </aside>
  );
}